import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { queryClient } from "pages/Root";
import {
  FilterAndSortListings,
  ShopListingModel,
} from "pages/root/shop/FilterAndSortListings";
import { getFeatureFlag } from "services/Flags";
import {
  getDiscountCustomColor,
  getDiscountText,
  getListingsWithDiscountInformation,
  hasLinearPricing,
} from "services/ListingHelperService";
import { notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const getGiftCardPriceRangeString = (
  giftCard: Models<"DisplayGiftCardListing">
) => {
  if (giftCard.giftCards.length === 0) {
    return "$0";
  }

  const costs = giftCard.giftCards.map((gc) => gc.baseCostInCents);
  const minCost = Math.min(...costs);
  const maxCost = Math.max(...costs);

  if (minCost === maxCost) {
    return `From $${minCost / 100}`;
  }

  return `Choose from $${minCost / 100}-$${maxCost / 100}`;
};

const getGiftCardsWithDiscountInformation = (
  giftCards: Models<"DisplayGiftCardListing">[],
  listingDiscounts: {
    [key: string]: Models<"ShopProductDiscountModel"> | undefined;
  } = {},
  customShopCategories: Models<"CategoryListingsResult">[] | undefined
): ShopListingModel[] => {
  if (!giftCards || !giftCards.length) return [];
  return giftCards.map((giftCard) => {
    const discountData = listingDiscounts[giftCard.id];
    const priceRange = getGiftCardPriceRangeString(giftCard);

    return {
      id: giftCard.id,
      title: "The Ultimate Gift of Care",
      description:
        "Give an experience that lingers long after the moment has passed. Our luxury gift cards are designed for those who deserve the very best.",
      imageUrl:
        giftCard.imageUri.substring(0, giftCard.imageUri.lastIndexOf("/") + 1) +
        "giftcard-justforyou.webp",
      type: "GiftCard",
      concernIds: [],
      areaIds: [],
      baseCost: 0,
      discountText: discountData ? getDiscountText(discountData) : "",
      discount: discountData,
      benefits: [],
      customColor: getDiscountCustomColor(discountData, customShopCategories),
      created: giftCard.createdAt,
      membershipCost: null,
      sellVolume: 0,
      priceRange: priceRange,
    };
  });
};

const loader = async ({ categoryId }: { categoryId: string }) => {
  const shopData = queryClient.fetchQuery(["/shop"], () => get("/shop"));
  const customCategoryListings = queryClient.fetchQuery(
    ["/shop/custom-categories/{categoryId}/listings"],
    () => get("/shop/custom-categories/{categoryId}/listings", { categoryId }),
    { staleTime: 1_000 }
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });
  const memberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );
  const listings = queryClient.fetchQuery(["/shop/listings"], () =>
    get("/listings")
  );

  const giftCardsFeatureFlag = await getFeatureFlag("giftcards");
  const platform = await queryClient.fetchQuery(["/platform/current"], () =>
    get("/platform/current")
  );

  const giftCardsEnabled =
    giftCardsFeatureFlag && platform?.platformSettings?.giftCardEnabled;

  const giftCards = giftCardsEnabled
    ? await queryClient.fetchQuery(["/gift-cards"], () =>
        get("/gift-cards", {})
      )
    : [];

  const recommendationIds = queryClient.fetchQuery(
    ["/adonis/top-recommendations/id"],
    () => get("/adonis/top-recommendations/id", { limit: 10 }),
    { staleTime: 1000 * 30 }
  );

  return waitObj({
    customCategoryListings,
    shopData,
    shopDiscounts,
    user,
    memberships,
    listings,
    giftCards,
    recommendationIds,
  });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const shopCustomCategoryLoader: LoaderFunction = ({
  params: { id: categoryId },
}) => {
  if (!categoryId) {
    return redirect("/home");
  }
  return loader({ categoryId });
};

const ShopCustomCategoryPage = () => {
  const navigate = useNavigate();
  const {
    customCategoryListings,
    shopData,
    shopDiscounts,
    user,
    memberships,
    recommendationIds,
    listings: allShopListings,
    giftCards,
  } = useLoaderData() as LoaderData;
  const customCategoryIncludedPackageListingIds =
    customCategoryListings.categoryInclusions.map((ci) => ci.packageListingId);

  const recommendedIds = new Set(recommendationIds);

  const packageAndTreatmentShopListings = getListingsWithDiscountInformation(
    allShopListings.filter((l) =>
      customCategoryIncludedPackageListingIds.includes(l.id)
    ),
    [customCategoryListings],
    shopDiscounts?.listingDiscounts
  );

  const giftCardShopListings = getGiftCardsWithDiscountInformation(
    giftCards?.filter((gc) =>
      customCategoryListings.categoryInclusions.some(
        (inclusion) => inclusion.packageListingId === gc.id
      )
    ) ?? [],
    shopDiscounts?.listingDiscounts,
    [customCategoryListings]
  );

  const categoryMembershipIds = customCategoryListings.categoryInclusions
    .map((ci) => ci.membershipId)
    .filter(notNull);

  const membershipsListings = memberships
    .filter((m) => categoryMembershipIds.includes(m.id))
    .map((m) =>
      mapMembershipToShopListing(m, customCategoryListings, shopDiscounts)
    );

  const listings = customCategoryListings.categoryInclusions
    .map((ci) => {
      if (ci.membershipId) {
        return membershipsListings.find((m) => m.id === ci.membershipId);
      } else if (
        ci.packageListingId &&
        giftCards?.some((gc) => gc.id === ci.packageListingId)
      ) {
        return giftCardShopListings.find((gc) => gc.id === ci.packageListingId);
      } else {
        return packageAndTreatmentShopListings.find(
          (p) => p.id === ci.packageListingId
        );
      }
    })
    .filter(notNull);

  const types = [...new Set(listings.map((listing) => listing.type))];
  const filteredConcerns =
    shopData.concerns?.filter((concern) =>
      listings.flatMap((listing) => listing.concernIds).includes(concern.id)
    ) ?? [];
  const filteredAreas =
    shopData.areas?.filter((area) =>
      listings.flatMap((listing) => listing.areaIds).includes(area.id)
    ) ?? [];

  return (
    <div className="flex flex-col bg-two">
      <ShopCustomCategoryPageHeader
        category={customCategoryListings.category}
      />
      {!!listings.length && (
        <FilterAndSortListings
          types={types}
          areas={filteredAreas}
          concerns={filteredConcerns}
          listings={listings}
        >
          {(listing) => {
            const isGiftCard = giftCardShopListings.some(
              (gc) => gc.id === listing.id
            );

            return (
              <ProductCardLarge
                key={listing.id}
                itemId={listing.id}
                title={listing.title}
                description={listing.description}
                imageUrl={listing.imageUrl}
                type={listing.type}
                tag=""
                concerns={filteredConcerns
                  .filter((concern) => listing.concernIds.includes(concern.id))
                  .map((concern) => concern.name)
                  .filter(notNull)}
                price={listing.baseCost}
                memberPrice={listing.membershipCost}
                onClick={() => {
                  if (listing.type === "Membership") {
                    navigate(`/pdp/membership/${listing.id}`);
                  } else if (
                    listing.type === "Product" ||
                    listing.type === "ProductFromExternalSource"
                  ) {
                    navigate(`/pdp/product/${listing.id}`);
                  } else {
                    navigate(`/pdp/package/${listing.id}`);
                  }
                }}
                hasLinearPricing={hasLinearPricing(
                  listing.packages?.flatMap((p) => p.packagePrices)
                )}
                unitType={listing.packageUnitType?.singularUnitType}
                discount={listing.discountText}
                customColor={listing.customColor}
                guestMode={user?.isGuest}
                priceRange={isGiftCard ? listing.priceRange : null}
                isRecommended={recommendedIds.has(listing.id)}
              />
            );
          }}
        </FilterAndSortListings>
      )}
      <RepeatMDFooter />
    </div>
  );
};

const ShopCustomCategoryPageHeader = ({
  category,
}: {
  category: Models<"CategoryModel">;
}) => (
  <ShopListHeader
    title={category.title ?? ""}
    subtitle={category.description ?? ""}
    backgroundImageUrl={category.headerImageUrl ?? ""}
    bgGradient
  />
);

const mapMembershipToShopListing = (
  membership: Models<"MembershipModel">,
  categoryListingsResult: Models<"CategoryListingsResult">,
  shopDiscounts?: Models<"ShopDiscountModel">
): ShopListingModel => {
  return {
    id: membership.id,
    title: membership.name,
    description: membership.description ?? "",
    imageUrl: membership.imageUrl,
    type: "Membership",
    concernIds: [],
    areaIds: [],
    baseCost: membership.monthlyCost,
    discountText: getDiscountText(
      shopDiscounts?.membershipDiscounts[membership.id]
    ),
    discount: shopDiscounts?.membershipDiscounts[membership.id],
    benefits: membership.membershipBenefits,
    customColor: getDiscountCustomColor(
      shopDiscounts?.membershipDiscounts[membership.id],
      [categoryListingsResult]
    ),
    created: membership.created,
    membershipCost: null,
    sellVolume: 0,
  };
};

export default ShopCustomCategoryPage;
